const _ = require("lodash");
const {escapeRegex} = require("../../../shared/StringUtil");
const {toLatin} = require("../../../shared/Numbers");
const Parser = require("./Parser");
const ParserHelpers = require("./util/ParserHelpers");
const Operators = require("./Operators");
const FastCanvasMath = require("./FastCanvasMath");
const CommandUtil = require("./util/CommandUtil");

const CommandIndex = require("./CommandIndex");
const CommandIndexDefinitions = _.mapValues(CommandIndex.nodes, node =>
	_.isFunction(node.readAloud) ? {special: true} : node.readAloud
);
const CommandIndexSpecialFunctions = _.pickBy(
	_.mapValues(CommandIndex.nodes, node =>
		_.isFunction(node.readAloud) ? node.readAloud : null
	),
	(value, key) => value !== null
);

// For now, we haven't found a good bit of text that causes the read aloud voices
// to pause that doesn't also cause others to read the name of the punctuation used.
// If we discover one, we insert it here.
const PAUSE_MARKER = " ";

// Some tokens to allow adjacent nodes to recognize eachother.
const FRACTION_START_MARKER = "FRACTION_START_TOKEN";
const NUMBER_BEFORE_FRACTION_REGEX = /(\d+) +FRACTION_START_TOKEN/;
const DOLLAR_REGEX = /^\$(\d+)/;

/**
 * This file contains functions that describe how to take a string of Batex and
 * turn into a readable string to pass to a speech API. This involves doing
 * the usual parsing and processing the nodes of the parse tree.
 */

/**
 * Given a props object like what would be passed to the main CanvasMath
 * methods (e.g. props.batex is a string with the main contents), return the
 * transcribed version to pass to speech APIs.
 *
 * @param {object} props
 * @returns {string}
 */
const readAloudBatexText = function(props) {
	let batex, ignoreIfAllMath;
	if (!props || typeof props === "string") {
		batex = props || "";
		props = {batex};
	} else {
		batex = props.batex;
		ignoreIfAllMath = props.ignoreIfAllMath;
	}

	const fastParsed = !props.noFast && FastCanvasMath.parseBatex(batex);
	if (fastParsed) {
		if (fastParsed.parseType === "math" && ignoreIfAllMath) return "";
		const fastText = processTextForReadaloud(fastParsed.text, {
			math: fastParsed.parseType === "math",
		});
		const processedFastText = postProcessReadAloudText(fastText);
		return collapseSpaces(
			processedFastText.trim().replace(/\n\n+/g, " " + PAUSE_MARKER)
		);
	}
	const parseResult = Parser.parseBatex(batex, {
		customCommands: props.customCommands,
		customEnvironments: props.customEnvironments,
	});
	if (ignoreIfAllMath && isAllMath(parseResult.tree)) return "";
	const parsedText = readAloudBatexTree(parseResult.tree);
	const processedText = postProcessReadAloudText(parsedText);
	return collapseSpaces(processedText);
};

/**
 * Similar to readAloudBatexText, but instead the argument is the root node
 * of the tree for an already-parsed Batex string. You can also include props
 * to be passed along.
 *
 * @param {object} tree
 * @param {object} raProps
 * @returns {string}
 */
const readAloudBatexTree = function(tree, raProps = {}) {
	const props = _.extend({}, raProps, {math: false});
	const parsedText = readAloudBatexNode(tree, props);
	const processedText = postProcessReadAloudText(parsedText);
	return collapseSpaces(processedText);
};

/**
 * Collapses multiple spaces into single spaces.
 *
 * @param {string} text
 * @returns {string}
 */
const collapseSpaces = function(text) {
	if (_.isNull(text)) return null;
	return text.replace(/\s+/g, " ").trim();
};

const logErrorAndGetReturnValue =
	CommandUtil.logReadAloudErrorAndGetReturnValue;

const getOrdinalStringForInteger = function(int) {
	if (_.isNaN(parseInt(int, 10))) {
		return logErrorAndGetReturnValue(
			`invalid value supplied to getOrdinal: ${int}`,
			"E_READ_ALOUD_GET_ORDINAL_OF_NON_NUMBER"
		);
	} else {
		return toLatin(int, true);
	}
};

/**
 * List of node types that cause a break in numbers, where they shouldn't. For
 * example, \bold{4}2 sohuld be read as 42, not 4 and then 2.
 * T57624
 */
const CONTIGUOUS_NUMBER_NODES = [
	"bold",
	"italic",
	"underline",
	"color",
	"fakeblank",
];

const causesNumberBreak = function(node) {
	return _.includes(CONTIGUOUS_NUMBER_NODES, node.type);
};

/**
 * Constant with node types and data for how to handle them.
 *
 * 1) null can be passed to not read the entire section
 * 2) Strings are simply read and ignore any children.
 * 3) Children with prefix or suffix or special handlers can be set using an object
 *
 * Object properties for option 3:
 * - children: set to true if you want the text from the child nodes read
 * - prefix: string to be read before text from child nodes
 * - suffix: string to be read after text from child nodes
 * - collapseAdjacentTextNodes: set to true if you want to remove spaces between
 *                              adjacent text nodes in first level of child nodes
 * - special: set to true if you want to do something more complicated, then
 *            define the special handling inside the special condition in the
 *            readAloudBatexNode function.
 *
 * Examples:
 * "": ignore the node and its children.
 * "point": reads "point" and ignores its children.
 * {children: true}: just reads the string from the children.
 * {children: true, prefix: "square root of"}: reads "square root of" followed
 *                                             by the children node text
 * {special: true}: uses whatever handler is defined inside readAloudBatexNode
 */

const DEFINED_NODE_TYPES = Object.freeze({
	absoluteValue: {children: true, prefix: "the absolute value of"},
	align: {special: true},
	alignMath: {special: true},
	alignLine: {special: true},
	angle: "angle",
	arrow: "",
	arrowList: "",
	balanceScale: "",
	baseBlank: "",
	bigDecimal: "point",
	// Getting blank context likely very hard, hope this is enough
	blank: "blank",
	// May be able to add alt text feature to diagrams, until then
	// this will have to suffice
	blockDiagram: "",
	blockList: {children: true},
	blocks: "",
	blockMath: {special: true},
	boxdigits: {special: true},
	btenblocks: "",
	cancel: null,
	center: {children: true, suffix: PAUSE_MARKER},
	cents: "cents",
	clock: "",
	coins: "",
	coloredDice: "",
	colorPositionText: {children: true, collapseAdjacentTextNodes: true},
	customCommand: "",
	customEnvironment: "",
	customInlineEnvironment: "",
	dayArrowSequence: "",
	decimalArrowDiagram: "",
	decimalMovement: "",
	digitcontainer: {children: true},
	distributionArrows: "",
	dots: "",
	error: "",
	fakeblank: {special: true},
	fraction: {special: true},
	graduatedCylinder: "",
	hundredsChartGrid: "",
	hundredsChartSquare: "",
	image: {special: true},
	indent: {children: true},
	inputCircleButton: "",
	item: {children: true},
	itemList: {special: true},
	lineBreak: "",
	longDivision: "",
	longDivisionBracket: "",
	longMultiplication: "",
	longSimpleOperation: "",
	math: {special: true, collapseAdjacentTextNodes: true},
	mcnugget: "",
	measuringCup: "",
	mequal: "equals",
	minus: "minus",
	mirror: {children: true},
	mistake: {prefix: "Common Mistake ;", children: true},
	modibot: "",
	neg: "negative",
	nonmath: {special: true},
	numberAverage: "",
	numberline: "",
	operator: {special: true},
	optree: "",
	optreeRow: "",
	paragraph: {children: true},
	parbox: {children: true},
	parentheses: {children: true, prefix: "(", suffix: ")"},
	phantom: "",
	pirateNumber: {special: true},
	previewI: {childen: true},
	plus: "plus",
	quad: "",
	shapeNumberCard: "",
	space: "",
	sqrt: {children: true, prefix: "the square root of"},
	stack: "",
	superscript: {special: true},
	table: {special: true},
	tableRow: {special: true},
	text: {special: true},
	textShape: {special: true},
	thermometer: "",
	triangle: "triangle",
	underlabel: "",
	units: {special: true},
	unsortedPirateNumber: {special: true},
	varbox: {special: true},
	variable: {special: true},
	vdots: "",
	wrapping: {special: true},
	wrappingItem: {children: true},
	zigzagPuzzle: "",
	...CommandIndexDefinitions,
});

/**
 * Processes wrapped text to allow bolding of single letters
 * inside of words while maintaining the proper reading
 *
 * We want to be able to bold letters in a word and have it be read out normally
 * Eg: \bold{M}egan should be read out as "Megan"
 *
 * Basically, if \bold, \italic, or \underline wraps letters with
 * no spaces before or after, those will be combined with the text
 * that appears before and/or after
 *
 * To address this, we keep track of the text from the current node
 * Every node's content is read, unless the node is a space node
 *   in which case the content is just empty
 *
 * @param {*} wrapperNode
 * @param {*} raProps
 * @returns {string} text to be read out
 */
const processWrappedText = function(wrapperNode, raProps) {
	let currentWord = "";
	const newChildren = [];
	const {children} = wrapperNode;

	for (let i = 0; i < children.length; ++i) {
		const thisNode = children[i];
		const isSpaceNode = ParserHelpers.isSpaceNode(thisNode);

		/**
		 * If the current node is a space node, then we store the current node and clear it.
		 * Otherwise:
		 *   If the current node is text or bold, italic, or underlined then we append the current node's content to our current word
		 *   If the node is any other node type, we read the current node and store it
		 *   Finally, if we have any word accumulated and we've reached another type of node,
		 *     the text content ended. We then store and clear the current word, if there is one
		 */
		if (isSpaceNode) {
			newChildren.push(currentWord);
			currentWord = "";
			continue;
		}

		const causesBreak = causesNumberBreak(thisNode);

		// If this text is bolded, italicized, or underlined
		// then we don't want to include units because
		// a single 's' would be replaced by 'seconds'
		const newRaProps = _.extend({}, raProps, {noUnits: causesBreak});

		// This is any batex that is not whitespace
		// Usually for node types that are not text, bold, italic, underline,
		// or any of the space types (space, lineBreak, or indent)
		// If this is a space node, then we'll just get an empty string
		const nodeContent = readAloudBatexNode(thisNode, newRaProps);

		if (causesBreak || thisNode.type === "text") {
			currentWord += nodeContent;
		} else {
			if (currentWord) {
				newChildren.push(currentWord);
				currentWord = "";
			}
			newChildren.push(nodeContent);
		}
	}

	/**
	 * Finally, we may have an accumulated word left over at the end, so we just store it.
	 * If the word was empty, it will be filtered out.
	 */
	newChildren.push(currentWord);
	return _.filter(newChildren).join(" ");
};

/**
 * Internal recursive helper that returns the transcribed string of a single
 * node of the tree and all its children. In addition to passing the node,
 * this also accepts an raProps object shaped as follows:
 * - math: boolean indicating whether we are in math mode.
 *
 * raProps should not be mutated. If you need to pass an updated version to
 * child notes, update it immutably.
 *
 * @param {object} node
 * @param {object} raProps
 * @returns {string}
 */
const readAloudBatexNode = function(node, raProps = {}) {
	const nodeDefinition = DEFINED_NODE_TYPES[node.type];
	if (node.readAloudText !== undefined) {
		// Allow for an override text to be added to nodes
		return processTextForReadaloud(node.readAloudText, raProps);
	} else if (_.isNull(nodeDefinition)) {
		return null;
	} else if (nodeDefinition !== undefined) {
		if (typeof nodeDefinition === "string") {
			return nodeDefinition;
		} else if (nodeDefinition.special) {
			// Note: if you're using the child nodes, check it for null. If the
			//       children return null it means there's an invalid command and
			//       you should return null for the current node to bubble up the
			//       error.
			if (["align", "alignMath"].includes(node.type)) {
				return node.read ? readAloudBatexChildren(node, raProps, ". ") : "";
			} else if (node.type === "alignLine") {
				return node.read ? readAloudBatexChildren(node, raProps) : "";
			} else if (node.type === "math") {
				const newRAProps = _.extend({}, raProps, {math: true});
				return readAloudBatexChildren(node, newRAProps);
			} else if (node.type === "nonmath") {
				const newRAProps = _.extend({}, raProps, {math: false});
				return readAloudBatexChildren(node, newRAProps);
			} else if (node.type === "blockMath") {
				if (
					node.children &&
					node.children.length === 1 &&
					node.children[0].type === "readAloudOverride"
				) {
					return node.children[0].readAloudText;
				} else {
					return "";
				}
			} else if (node.type === "boxdigits") {
				return node.digits.join(" ");
			} else if (node.type === "operator") {
				const opText = Operators.computeText(node.text, node);
				return processTextForReadaloud(opText, raProps);
			} else if (node.type === "wrapping") {
				return processWrappedText(node, raProps);
			} else if (node.type === "text") {
				return processTextForReadaloud(node.text, raProps);
			} else if (node.type === "varbox") {
				return node.initialValue || "";
			} else if (node.type === "fakeblank") {
				if (node.initialValue) {
					if (node.initialValue === "-") {
						return "minus";
					}
					return node.initialValue;
				} else {
					return "blank";
				}
			} else if (node.type === "variable") {
				const variableText = _.get(node, "children.0.text") || "";
				if (!variableText) {
					return logErrorAndGetReturnValue(
						`unknown node ${node.type}`,
						"E_READ_ALOUD_NO_VARIABLE_TEXT",
						node
					);
				}
				const chars = variableText.split("");
				return _.map(chars, c => c.toUpperCase(c)).join(" ");
			} else if (node.type === "fraction") {
				const [numeratorNode, denominatorNode] = node.children;
				const numeratorText = (
					readAloudBatexNode(numeratorNode, raProps) || ""
				).trim();
				const denominatorText = (
					readAloudBatexNode(denominatorNode, raProps) || ""
				).trim();

				if (!numeratorText || !denominatorText) return null;

				const justDigits = /^\s*\d+\s*$/;

				const canShorthandNumerator = textAsWords => {
					// If we get 2 words back it might start with "negative"
					// but if we get 3+ then we definitely cannot
					// read this out in any special way

					if (textAsWords.length > 2) {
						return false;
					}
					// If we have 2 words back, the first one must start with
					// "negative" and the second must be just a number
					if (textAsWords.length === 2) {
						return (
							textAsWords[0] === "negative" && justDigits.test(textAsWords[1])
						);
					}

					// Now we just have 1 word, so it must be a number and nothing else
					// otherwise we read out normally
					return justDigits.test(textAsWords[0]);
				};

				const canShorthandDenominator = textAsWords => {
					// In this case, the denominator MUST be
					// JUST a number, with NO sign (so it cannot
					// start with "negative" like it could with
					// the numerator)
					return textAsWords.length === 1 && justDigits.test(textAsWords[0]);
				};

				const numeratorAsWords = _.words(numeratorText);
				const denominatorAsWords = _.words(denominatorText);

				const numeratorNum = parseInt(
					numeratorAsWords.length === 2
						? numeratorAsWords[1]
						: numeratorAsWords[0]
				);
				const denominatorNum = parseInt(
					denominatorAsWords.length === 2
						? denominatorAsWords[1]
						: denominatorAsWords[0]
				);

				// Don't shorthand read out the fraction if the denominator is just a 1
				// so we don't read out something like "3 1sts"

				// TODO: Adjust the max ordinal readout as needed (eg if we should read out "31 over 47" or "31 47ths")
				if (
					denominatorNum !== 1 &&
					numeratorNum <= 99 &&
					(denominatorNum <= 99 || denominatorNum % 10 === 0) &&
					canShorthandNumerator(numeratorAsWords) &&
					canShorthandDenominator(denominatorAsWords)
				) {
					// Now if we're able to "shorthand" this then we will get the ordinal for
					// the numerator and denominator and include a plural suffix if necessary
					let denominatorOrdinal = getOrdinalStringForInteger(denominatorNum);
					if (denominatorNum === 2) {
						denominatorOrdinal = "half";
					}

					const denomSuffix = Math.abs(numeratorNum) > 1 ? "s" : "";
					return `${FRACTION_START_MARKER} ${numeratorText} ${denominatorOrdinal}${denomSuffix}`;
				}

				return `${numeratorText} over ${denominatorText}`;
			} else if (node.type === "superscript") {
				const superscriptText = readAloudBatexChildren(
					node.children[1],
					raProps
				);

				// Make sure the base text is supported
				const baseText = readAloudBatexNode(node.children[0], raProps);
				if (_.isNull(baseText)) {
					return logErrorAndGetReturnValue(
						`unsupported read aloud content in power base`,
						"E_READ_ALOUD_POWER_BASE_UNSUPPORTED"
					);
				}

				// Ordinal suffixes
				const ordinalSuffixes = ["st", "nd", "rd", "th"];

				// Get the ordinal name for the power
				if (!superscriptText) {
					return logErrorAndGetReturnValue(
						`empty power in superscript node`,
						"E_READ_ALOUD_EMPTY_SUPERSCRIPT_NODE"
					);
				} else if (superscriptText === "2") {
					return baseText + " squared";
				} else if (/^\d+$/.test(superscriptText)) {
					return (
						baseText +
						` to the ${getOrdinalStringForInteger(superscriptText)} power`
					);
				} else if (_.includes(ordinalSuffixes, superscriptText)) {
					return baseText + superscriptText;
				} else {
					return baseText + ` to the power of ${superscriptText}`;
				}
			} else if (node.type === "image") {
				return node.altText;
				// If use critter list if we want to read critter icons
				// } else if (node.type === "critter") {
				// 	return critterToReadText[critterList[node.name]];
			} else if (node.type === "itemList") {
				const processedItems = node.children.map((c, i) => {
					return readAloudBatexNode(c, raProps);
				});
				if (_.some(processedItems, i => _.isNull(i))) return null;
				return processedItems.join(" ;; ");
			} else if (
				node.type === "pirateNumber" ||
				node.type === "unsortedPirateNumber"
			) {
				const {pirateArr} = node;
				if (pirateArr && pirateArr.length === 1) {
					const pn = pirateArr[0];
					if (pn === "o") {
						return "dot";
					} else if (pn === "x" || pn === "c") {
						return pn.toUpperCase();
					} else {
						return "";
					}
				} else if (pirateArr.length) {
					return "";
				}
				return logErrorAndGetReturnValue(
					`empty pirate array for pirate number`,
					"E_READ_ALOUD_EMPTY_PIRATE_NUMBER_ARRAY"
				);
			} else if (node.type === "table") {
				return node.extraOptions.read
					? readAloudBatexChildren(node, raProps, " . ")
					: "";
			} else if (node.type === "tableRow") {
				return node.extraOptions.read
					? readAloudBatexChildren(node, raProps)
					: "";
			} else if (node.type === "textShape") {
				const {shape} = node;
				return `${shape}`;
			} else if (CommandIndexSpecialFunctions[node.type]) {
				return CommandIndexSpecialFunctions[node.type](node, {
					raProps,
					readAloudBatexNode,
					readAloudBatexChildren,
					logErrorAndGetReturnValue,
				});
			} else {
				return logErrorAndGetReturnValue(
					`no handler for node type ${node.type}`,
					"E_READ_ALOUD_NO_HANDLING_FOR_SPECIAL_NODE_TYPE"
				);
			}
		} else if (nodeDefinition.children) {
			let readAloudText = "";
			const childText = readAloudBatexChildren(node, raProps);
			if (_.isNull(childText)) return null;
			if (nodeDefinition.prefix) readAloudText += nodeDefinition.prefix + " ";
			readAloudText += childText;
			if (nodeDefinition.suffix) readAloudText += " " + nodeDefinition.suffix;
			return readAloudText;
		} else {
			return logErrorAndGetReturnValue(
				`bad node definition for node type ${node.type}`,
				"E_READ_ALOUD_INVALID_NODE_DEFINITION",
				nodeDefinition
			);
		}
	} else {
		return logErrorAndGetReturnValue(
			`no node definition for node type ${node.type}`,
			"E_READ_ALOUD_INVALID_NODE"
		);
	}
};

/**
 * Internal recursive helper that returns the transcribed string of the
 * children of a node in the tree. Accepts the same raProps that
 * readAloudBatexNode does.
 *
 * @param {object} node
 * @param {object} raProps
 * @returns {string}
 */
const COLLAPSIBLE_NODES = [...CONTIGUOUS_NUMBER_NODES, "text"];
const readAloudBatexChildren = function(node, raProps = {}, joinText = " ") {
	if (node && node.children && node.children.length) {
		const nodeDefinition = DEFINED_NODE_TYPES[node.type];
		if (_.isNull(nodeDefinition)) return null;

		const childrenToRead = [];
		const addChildToRead = (n, textOverride) => {
			const ratext =
				textOverride !== undefined
					? textOverride
					: readAloudBatexNode(n, raProps);
			if (ratext || ratext === null) {
				childrenToRead.push({ratext, node: n});
			}
		};

		// reorder children as necessary (long div bracket should read dividend first)
		// and get read aloud text
		for (let i = 0; i < node.children.length; i++) {
			const curr = node.children[i];
			const next = node.children[i + 1];
			if (next && next.type === "longDivisionBracket") {
				// skip long division brackets
				i += 2;
			} else if (
				next &&
				curr.type === "lineBreak" &&
				next.type === "lineBreak"
			) {
				addChildToRead(curr, " " + PAUSE_MARKER);
				while (
					node.children[i + 1] &&
					node.children[i + 1].type === "lineBreak"
				) {
					i++;
				}
			} else {
				addChildToRead(curr);
			}
		}

		if (_.some(childrenToRead, child => _.isNull(child.ratext))) {
			return null;
		} else {
			let returnText = "";
			for (let t = 0; t < childrenToRead.length; t++) {
				const currText = childrenToRead[t].ratext;
				const currNode = childrenToRead[t].node;
				const prevNode = childrenToRead[t - 1] && childrenToRead[t - 1].node;
				// Collapse spaces between text nodes in math mode
				if (t === 0) {
					returnText += currText;
				} else {
					const suppressJoinText =
						// only true in math mode at time of writing
						// if parent node collapses children, suppress spaces between text
						nodeDefinition.collapseAdjacentTextNodes &&
						// both previous and current nodes are collapsible
						prevNode &&
						COLLAPSIBLE_NODES.includes(currNode.type) &&
						COLLAPSIBLE_NODES.includes(prevNode.type);

					const usedJoinText = suppressJoinText ? "" : joinText;
					returnText += usedJoinText + currText;
				}
			}
			return returnText;
		}
	} else {
		return "";
	}
};

/**
 * Direct mappings for the icon command/node type.
 */
// eslint-disable-next-line no-unused-vars
const ICON_MAPPING = {
	activity: "activity",
	"activity-deep-gold": "activity",
	"arrow-down": "down arrow",
	"arrow-with-tail": "down arrow",
	"BA-white": "BA logo",
	"BA-full-white": "BA logo",
	back: "back arrow",
	baHome: "BA home icon",
	"baHome-yellow": "BA home icon",
	beam: "BEAM bridge logo",
	bell: "classroom bell",
	"bell-blue": "classroom bell",
	"bell-theater": "classroom bell",
	"bell-white": "classroom bell",
	"book-blue": "book icon",
	"book-white": "book icon",
	book: "book icon",
	"bookmark-black": "bookmark icon",
	"bookmark-white": "bookmark icon",
	bookmark: "bookmark icon",
	"bug-red": "bug icon",
	bug: "bug icon",
	"checkbox-beige": "checkbox",
	"checkbox-checked-beige": "checked checkbox",
	"checkbox-checked": "checkbox",
	"circle-bell-theater": "classroom bell button",
	"circle-bookmark": "bookmark button",
	"circle-chapter-1": "chapter 1 button",
	"circle-chapter-2": "chapter 2 button",
	"circle-chapter-3": "chapter 3 button",
	"circle-chapter-4": "chapter 4 button",
	"circle-chapter-5": "chapter 5 button",
	"circle-chapter-6": "chapter 6 button",
	"circle-chapter-7": "chapter 7 button",
	"circle-chapter-8": "chapter 8 button",
	"circle-chapter-9": "chapter 9 button",
	"circle-chapter-10": "chapter 10 button",
	"circle-chapter-11": "chapter 11 button",
	"circle-chapter-12": "chapter 12 button",
	"circle-index": "index button",
	"circle-library": "book button",
	"circle-play": "play button",
	"circle-puzzle": "puzzle button",
	"close-active": "X button",
	"close-demo": "X button",
	close: "X button",
	"demo-next": "next arrow",
	"down-blue": "down triangle",
	"down-cyan": "down triangle",
	"down-profile": "down triangle",
	download: "download icon",
	"eraser-on": "eraser",
	eraser: "eraser",
	exit: "X button",
	"help-black": "question mark icon",
	"help-white": "question mark icon",
	help: "question mark icon",
	"home-white": "home icon",
	home: "home icon",
	homework: "homework icon",
	"index-demo": "index icon",
	index: "index icon",
	"info-black": "info icon",
	"info-white": "info icon",
	info: "info icon",
	"left-profile": "left triangle",
	left: "left triangle",
	"lock-key": "key",
	"lock-locked-gray": "lock",
	"lock-locked-white": "lock",
	"lock-locked": "lock",
	"lock-tilted": "lock",
	lock1: "lock",
	lock2: "lock",
	"locked-unlocked-gray": "unlocked lock",
	"locked-unlocked": "unlocked lock",
	logout: "logout icon",
	new: "new icon",
	"observatory-blue": "observatory icon",
	"observatory-white": "observatory icon",
	observatory: "observatory icon",
	"octagon-half": "half octagon",
	"octagon-white": "octagon",
	octagon: "octagon",
	"orange-arrow": "orange arrow",
	"partial-progress": "partially filled octagon",
	"pencil-on": "pencil",
	pencil: "pencil",
	"play-black": "play button",
	"play-white": "play button",
	"profile-default": "default student icon",
	"projector-blue": "projector icon",
	"projector-white": "projector icon",
	projector: "projector icon",
	"puzzle-blue": "puzzle piece",
	"puzzle-white": "puzzle piece",
	"puzzle-yellow": "puzzle piece",
	ribbon: "number one ribbon icon",
	"right-profile": "right triangle",
	"right-yellow": "right triangle",
	right: "right triangle",
	"settings-white": "gear icon",
	settings: "gear icon",
	"surround-white": "student icon frame",
	surround: "student icon frame",
	"teacher-blue": "apple",
	"teacher-cyan": "apple",
	teacher: "apple",
	team: "BA monsters",
	"trophy-active": "trophy",
	"trophy-inactive": "trophy",
	"up-profile": "up triangle",
	"zoom-in": "zoom in icon",
	"zoom-out": "zoom out icon",
};

/**
 * NOTE: these are parsed as Regex matches due to the way spokenUnits' keys are
 * used.
 */
const spokenUnits = {
	// Length
	m: "meters",
	km: "kilometers",
	cm: "centimeters",
	mm: "millimeters",
	ft: "feet",
	in: "inches",
	mi: "miles",
	yd: "yards",

	// Area
	"sq[.]? m": "square meters",
	"sq[.]? km": "square kilometers",
	"sq[.]? cm": "square centimeters",
	"sq[.]? mm": "square millimeters",
	"sq[.]? ft": "square feet",
	"sq[.]? in": "square inches",
	"sq[.]? mi": "square miles",
	"sq[.]? yd": "square yards",

	// Volume
	"cu[.]? m": "cubic meters",
	"cu[.]? km": "cubic kilometers",
	"cu[.]? cm": "cubic centimeters",
	"cu[.]? mm": "cubic millimeters",
	"cu[.]? ft": "cubic feet",
	"cu[.]? in": "cubic inches",
	"cu[.]? mi": "cubic miles",
	"cu[.]? yd": "cubic yards",

	// Mass
	g: "grams",
	mg: "milligrams",
	kg: "kilograms",

	// Time
	s: "seconds",
	min: "minutes",
	hr: "hours",
};

const unitsAbbreviations = (function() {
	const ua = _.clone(spokenUnits);
	_.each(["in", "min"], ambiguousUnitName => delete ua[ambiguousUnitName]);
	ua.sq = "square";
	ua.cu = "cubic";
	return ua;
})();

const unitsAbbreviationsRE = (function() {
	const acceptableLastChars = [".", ","];
	return new RegExp(
		"^(" +
			Object.keys(unitsAbbreviations).join("|") +
			")([" +
			acceptableLastChars.join("") +
			"]?)$",
		"i"
	);
})();

/**
 * Object mapping operators returned from Operators.computeText to a text
 * transcription. The keys should also include operators it doesn't transform.
 */
const operatorToReadTextForMath = {
	"+": " plus ",
	"-": " negative ",
	"\u2212": " minus ",
	"\u00D7": " times ",
	"\u2022": " times ",
	"\u00F7": " divided by ",
	"=": " equals ",
	"\u2248": " approximately equals ",
	"\u2260": " does not equal ",
	"<": " is less than ",
	">": " is greater than ",
};
const operatorPatternForMath = new RegExp(
	Object.keys(operatorToReadTextForMath)
		.map(x => escapeRegex(x))
		.join("|"),
	"g"
);
const operatorToReadTextForNonMath = {
	"+": " plus ",
	"\u00F7": " divided by ",
	"=": " equals ",
	"\u2248": " approximately equals ",
	"\u2260": " does not equal ",
	"<": " less than",
	">": " greater than ",
};
const operatorPatternForNonMath = new RegExp(
	Object.keys(operatorToReadTextForNonMath)
		.map(x => escapeRegex(x))
		.join("|"),
	"g"
);

// // Critter List
// const critterList = {
// 	al: "alligopher",
// 	bu: "buffalobster",
// 	cp: "chimpanda",
//  cb: "chimbanda",
// 	cm: "chipmonkey",
// 	fl: "flamingoat",
// 	hi: "hippopotamoose",
// 	oc: "octopug",
// 	or: "orangutoad",
// 	pe: "pelicamel",
// 	po: "porcupuma",
// 	rh: "rhinocerabbit",
// 	sp: "spotted jackalope",
// 	ta: "tarantulemur",
// 	to: "tortusk",
// };

/**
 * Naive text replacements
 */
const critterToReadText = {
	alligopher: "Al-lee-gopher",
	buffalobster: "buff-uh-lobster",
	chimpanda: "chim-panda",
	chimbanda: "chim-banda",
	chipmonkey: "chip-monkey",
	flamingoat: "flemming-goat",
	hippopotamoose: "hippo-pot-uh-moose",
	octopug: "octo pug",
	orangutoad: "oh-rang-uh-toad",
	pelicamel: "pell-uh-camel",
	porcupuma: "pork-you-puma",
	rhinocerabbit: "rye-gnaws-uh-rabbit",
	"spotted jackalope": "spotted jack-uh-lope",
	tarantulemur: "tehran-chew-lemur",
	tortusk: "tor-tusk",
};
const critterPattern = new RegExp(
	Object.keys(critterToReadText)
		.map(x => escapeRegex(x))
		.join("|"),
	"gi"
);

const specialWordToReadText = {
	subtractile: "subtract-tile",
	cryptarithm: "crypt-uh-rhythm",
	crosstile: "cross-tile",
	kraken: "crack-ken",
	"\u00B0": " degrees ",
	combobot: "combo-bot",
	Combobot: "Combo-bot",
	fillomino: "fill-awe-min-oh",
	mismo: "mees-mow",
};

const specialWordPattern = new RegExp(
	Object.keys(specialWordToReadText)
		.map(x => escapeRegex(x))
		.join("|"),
	"gi"
);

const numericPossessivePattern = /\b\d+(,\d\d\d)*\s?(s|'s)\b/g;
const numericPossessiveToReadText = function(text) {
	let digits = text.replace(",", "");
	digits = digits.substring(
		0,
		_.findIndex(digits, c => !/\d/.test(c))
	);
	return convertNumberStringToWords(digits, {pluralized: true}) + " ";
};

/**
 * Converts integers to words.
 * TODO: expand to include decimals if needed
 */
const convertNumberStringToWords = function(digits, options = {}) {
	const {pluralized} = options;
	if (digits.length > 12) return digits + (pluralized ? "s" : "");
	if (digits === "0") return pluralized ? "zeroes" : "zero";

	const pluralize = function(text) {
		if (text.endsWith("six") || text.endsWith("zero")) {
			return text + "es";
		} else if (text.endsWith("y")) {
			// tens
			return text.substring(0, text.length - 1) + "ies";
		} else {
			return text + "s";
		}
	};

	const groupSuffixes = ["", "thousand", "million", "billion"];
	const d = {
		"0": "",
		"1": "one",
		"2": "two",
		"3": "three",
		"4": "four",
		"5": "five",
		"6": "six",
		"7": "seven",
		"8": "eight",
		"9": "nine",
	};
	const td = {
		"10": "ten",
		"11": "eleven",
		"12": "twelve",
		"13": "thirteen",
		"14": "fourteen",
		"15": "fifteen",
		"16": "sixteen",
		"17": "seventeen",
		"18": "eighteen",
		"19": "ninteen",
	};
	const tens = {
		"0": "",
		"2": "twenty",
		"3": "thirty",
		"4": "forty",
		"5": "fifty",
		"6": "sixty",
		"7": "seventy",
		"8": "eighty",
		"9": "ninety",
	};

	const convertGroup = function(group) {
		const padded = _.padStart(group, 3, "0");
		const hundredsDigit = padded[0];
		const tensDigit = padded[1];
		const onesDigit = padded[2];
		let groupText = "";

		if (hundredsDigit !== "0") {
			groupText += " " + d[hundredsDigit] + " hundred";
		}
		if (td[tensDigit + onesDigit]) {
			groupText += " " + td[tensDigit + onesDigit];
		} else {
			if (tens[tensDigit]) {
				groupText += " " + tens[tensDigit];
				if (tensDigit !== "0" && onesDigit !== "0") {
					groupText += "-" + d[onesDigit];
				} else {
					if (d[onesDigit]) groupText += " " + d[onesDigit];
				}
			} else {
				if (d[onesDigit]) groupText += " " + d[onesDigit];
			}
		}

		return groupText;
	};

	const numGroups = Math.ceil(digits.length / 3);
	const paddedDigits = _.padStart(digits, numGroups * 3, "0");
	let convertedText = "";
	for (let s = 0; s < numGroups; s++) {
		const startIndex = s * 3;
		const groupDigits = paddedDigits.substring(startIndex, startIndex + 3);
		const groupSuffix = groupSuffixes[numGroups - s - 1];
		convertedText += convertGroup(groupDigits);
		if (groupSuffix) convertedText += " " + groupSuffix;
	}

	return pluralized ? pluralize(convertedText) : convertedText;
};

/**
 * Internal helper that takes a string of text, which should be mostly readable
 * as-is, and applies final fixes to it for passing to a speech API. Takes the
 * same raProps that readAloudBatexNode does.
 *
 * @param {string} token Token in the text. "Ms. Q spends $100" is split into ["Ms.", "Q", "spends", "$100"]
 * @param {object} raProps
 * @returns {string}
 */
const processTextForReadaloud = function(token, raProps) {
	// Replace operators
	if (raProps.math) {
		token = token.replace(
			operatorPatternForMath,
			op => operatorToReadTextForMath[op]
		);
	} else {
		token = token.replace(
			operatorPatternForNonMath,
			op => operatorToReadTextForNonMath[op]
		);
	}

	// Simple replacements
	token = token.replace(
		critterPattern,
		cp => critterToReadText[cp.toLowerCase()]
	);

	token = token.replace(
		specialWordPattern,
		sp => specialWordToReadText[sp.toLowerCase()]
	);

	if (token.match(DOLLAR_REGEX)) {
		const amount = token.match(DOLLAR_REGEX)[1];
		token = token.replace(
			DOLLAR_REGEX,
			`${amount} dollar` + (amount > 1 ? "s" : "")
		);
	}

	// Unit abbreviations
	if (token.length > 0 && !raProps.noUnits) {
		const match = token.match(unitsAbbreviationsRE);
		if (match && unitsAbbreviations[match[1]]) {
			return unitsAbbreviations[match[1]] + match[2];
		}
	}

	// If text is in all caps, then each letter should be
	// read out individually
	// If the string contains no non-word characters
	// AND it contains only uppercase letters then it is
	// split so each letter is read out individually

	if (
		!/[^\w+][\.\?]/.test(token) &&
		/[A-Z]/.test(token) &&
		!/[a-z]/.test(token)
	) {
		// We only want to split all-caps words that are longer than 2 letters
		// and we want to preserve the location of any punctuation
		// at the end of the word (if there is any)
		token = token.replace(/([A-Z]{2,})[\.\?]?/, str => str.split("").join(" "));
	}

	return token;
};

const postProcessReadAloudText = function(text) {
	if (_.isNull(text)) return null;

	// Previous node structures.
	// Read numbers preceding a fraction with a number in between.
	// ex. 4 3/4 should be read 4 and 3 4ths
	while (NUMBER_BEFORE_FRACTION_REGEX.test(text)) {
		text = text.replace(NUMBER_BEFORE_FRACTION_REGEX, "$1 and");
	}
	while (text.includes(FRACTION_START_MARKER)) {
		text = text.replace(FRACTION_START_MARKER, "");
	}

	// Replace fancy quote and apostrophes with base characters
	text = text.replace(/’/g, "'");
	text = text.replace(/(“|”)/g, '"');

	// Possessives
	text = text.replace(numericPossessivePattern, npp =>
		numericPossessiveToReadText(npp)
	);

	// Simple replacements
	text = text.replace(
		critterPattern,
		cp => critterToReadText[cp.toLowerCase()]
	);

	return text;
};

/**
 * Internal helper for checking whether a batex field is entirely in math mode.
 * Used on engines to suppress reading some fields if they are all in math mode.
 *
 * @param {object} parsed batex node
 * @returns {bool}
 */
const isAllMath = node => {
	if (!node.children) {
		return false;
	} else {
		const filteredChildren = _.filter(
			node.children,
			c => !ParserHelpers.isSpaceNode(c)
		);
		if (filteredChildren.length === 1) {
			const childNode = filteredChildren[0];
			return childNode.type === "math" || isAllMath(childNode);
		} else {
			return false;
		}
	}
};

module.exports = {
	readAloudBatexText,
	readAloudBatexTree,
	critterToReadText,
};
